import { AxiosInstance, AxiosResponse } from "axios"
import {
  downloadResponseAsFile,
  downloadBase64AsFile,
} from "utils/files/downloadUtils"
import {
  SQL_EXPORT_TYPES,
  EXPORT_VARIABLE_TYPES,
  DATA_FORMAT,
  CSV_CAT_OPTION,
} from "types/engineExport.types"
import { QueryInstance } from "src/types/engine.types"
import { resolveAsyncBase64 } from "api/asyncJob"

/**
 * Export API functions - Refactored to use authenticated axios instance
 * Authentication headers are handled automatically by the axios instance
 */

/**
 * Exports data as a JSON file from the given instance and triggers a download.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param instanceID - The ID of the instance to export.
 * @param filename - The name to use for the downloaded file (without extension).
 * @param dataFormat - The data format (categorized or expanded).
 * @returns The file written (its exact bytes), or null when the download failed.
 * @throws Will throw an error if the request fails.
 */
export async function exportToJSON(
  axiosInstance: AxiosInstance,
  instanceID: QueryInstance,
  filename: string,
  dataFormat: DATA_FORMAT = DATA_FORMAT.CATEGORIZED
): Promise<File | null> {
  const config = {
    responseType: "blob" as const, // Important to download files as binary
  }

  const projectName = encodeURIComponent(filename || "export")
  const response = await axiosInstance.get(
    `/export-json?instanceId=${instanceID}&project=${projectName}&dataformat=${dataFormat}`,
    config
  )

  return downloadResponseAsFile(
    response,
    filename ? `${filename}.json` : "export.json"
  )
}

/**
 * Exports data as a JSONL (JSON Lines) zip file from the given instance and triggers a download.
 * The zip contains a schema.json and a .jsonl data file (one JSON object per respondent per line).
 *
 * @param axiosInstance - Authenticated axios instance
 * @param instanceID - The ID of the instance to export.
 * @param filename - The name to use for the downloaded file (without extension).
 * @returns The file written (its exact bytes), or null when the download failed.
 * @throws Will throw an error if the request fails.
 */
export async function exportToJSONL(
  axiosInstance: AxiosInstance,
  instanceID: QueryInstance,
  filename: string,
  useLabels: boolean = false,
  flatSeparator: string = ""
): Promise<File | null> {
  try {
    const config = {
      responseType: "text" as const,
    }

    const projectName = encodeURIComponent(filename || "export")
    const labelsParam = useLabels ? "&labels" : ""
    const flatParam = flatSeparator ? `&flat=${flatSeparator}` : ""
    const response = await axiosInstance.get(
      `/export-json?instanceId=${instanceID}&project=${projectName}&jsonl${labelsParam}${flatParam}`,
      config
    )

    return downloadBase64AsFile(response.data, `${filename || "export"}.zip`, "application/zip")
  } catch (error) {
    console.error("Error downloading JSONL file:", error)
    throw error
  }
}

/**
 * Exports data as a SQL file from the given instance and triggers a download.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param instanceID - The ID of the instance to export.
 * @param sqlType - The type of SQL export ("wide" or "narrow").
 * @param project - The project name associated with the SQL export.
 * @param filename - The name to use for the downloaded file (without extension).
 * @returns The response object from the API call.
 * @throws Will throw an error if the request fails.
 */
export async function exportToSQL(
  axiosInstance: AxiosInstance,
  instanceID: QueryInstance,
  sqlType: SQL_EXPORT_TYPES,
  project: string,
  filename: string
): Promise<AxiosResponse<Blob>> {
  const config = {
    responseType: "blob" as const, // Important to download files as binary
  }

  const response = await axiosInstance.get(
    `/export-sql?instanceId=${instanceID}&sqlType=${sqlType}&project=${project}`,
    config
  )

  downloadResponseAsFile(response, filename ? `${filename}.sql` : "export.sql")
  return response
}

/**
 * Exports data as an SPS file from the given instance and triggers a download.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param instanceID - The ID of the instance to export.
 * @param varType - The variable type to include in the export ("numbering" or "labels").
 * @param filename - The name to use for the downloaded file (without extension).
 * @returns The file written (its exact bytes), or null when the download failed.
 * @throws Will throw an error if the request fails.
 */
export async function exportToSPS(
  axiosInstance: AxiosInstance,
  instanceID: QueryInstance,
  varType: EXPORT_VARIABLE_TYPES,
  filename: string
): Promise<File | null> {
  try {
    const config = {
      responseType: "text" as const, // Expecting base64 encoded string
    }

    const response = await axiosInstance.get(
      `/export-sps?instanceId=${instanceID}&varType=${varType}&spsType=sps_dat&project=${encodeURIComponent(filename || "export")}`,
      config
    )

    return downloadBase64AsFile(response.data, `${filename || "export"}.zip`, "application/zip")
  } catch (error) {
    console.error("Error downloading zip file:", error)
    throw error
  }
}

// The async submit/poll/fetch loop lives in `api/asyncJob.ts` now that /data-integrate and the base check
// share it. Re-exported so existing import sites keep working.
export { resolveAsyncBase64 }

/**
 * Exports data as a SAV (SPSS binary) file from the given instance and triggers a download.
 * This produces a .sav file directly, without requiring SPSS to process SPS syntax.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param instanceID - The ID of the instance to export.
 * @param varType - The variable type to include in the export ("numbering" or "labels").
 * @param filename - The name to use for the downloaded file (without extension).
 * @returns The file written (its exact bytes), or null when the download failed.
 * @throws Will throw an error if the request fails.
 */
export async function exportToSAV(
  axiosInstance: AxiosInstance,
  instanceID: QueryInstance,
  varType: EXPORT_VARIABLE_TYPES,
  filename: string
): Promise<File | null> {
  try {
    const config = {
      responseType: "text" as const,
      headers: { "ASync-Request": 10 },
    }

    const projectName = encodeURIComponent(filename || "export")
    const response = await axiosInstance.get<string>(
      `/export-sps?instanceId=${instanceID}&varType=${varType}&project=${projectName}&sav`,
      config
    )

    return downloadBase64AsFile(
      await resolveAsyncBase64(axiosInstance, response),
      `${filename || "export"}.zip`,
      "application/zip"
    )
  } catch (error) {
    console.error("Error downloading SAV file:", error)
    throw error
  }
}

/**
 * Exports data as a DQD (DataQ Binary) file from the given instance and triggers a download.
 * DQD is DataQ's internal binary format for efficient data storage.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param instanceID - The ID of the instance to export.
 * @param filename - The name to use for the downloaded file (without extension).
 * @param dataFormat - The data format (categorized or expanded).
 * @returns The file written (its exact bytes), or null when the download failed.
 * @throws Will throw an error if the request fails.
 */
export async function exportToDQD(
  axiosInstance: AxiosInstance,
  instanceID: QueryInstance,
  filename: string,
  dataFormat: DATA_FORMAT = DATA_FORMAT.CATEGORIZED
): Promise<File | null> {
  try {
    const config = {
      responseType: "text" as const, // Expecting base64 encoded string
    }

    const response = await axiosInstance.get(
      `/export-dqd?instanceId=${instanceID}&dataformat=${dataFormat}`,
      config
    )

    return downloadBase64AsFile(response.data, `${filename || "export"}.dqd`, "application/octet-stream")
  } catch (error) {
    console.error("Error downloading DQD file:", error)
    throw error
  }
}

/**
 * Exports data as a CSV file from the given instance and triggers a download.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param instanceID - The ID of the instance to export.
 * @param varType - The variable type to include in the export ("numbering" or "labels").
 * @param catOption - The categorical export option.
 * @returns The response object from the API call.
 * @throws Will throw an error if the request fails.
 */
export async function exportToCSV(
  axiosInstance: AxiosInstance,
  instanceID: QueryInstance,
  varType: EXPORT_VARIABLE_TYPES = EXPORT_VARIABLE_TYPES.LABELS,
  catOption: CSV_CAT_OPTION = CSV_CAT_OPTION.NORMAL,
  filename: string = "export"
): Promise<AxiosResponse<string>> {
  try {
    const config = {
      responseType: "text" as const, // Expecting base64 encoded string
    }

    const response = await axiosInstance.get(
      `/export-csv?instanceId=${instanceID}&varType=${varType}&cat_option=${catOption}&project=${encodeURIComponent(filename || "export")}`,
      config
    )

    return response
  } catch (error) {
    console.error("Error downloading CSV file:", error)
    throw error
  }
}
